import styles from './EntityPicker.module.scss';

import { Stack } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useCallback, useMemo, useState } from 'react';

import { IResponse } from '@work4all/data';

import { TenantBankAccount } from '@work4all/models/lib/Classes/TenantBankAccount.entity';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { Tooltip } from '../tooltip/Tooltip';

import { EntityPicker } from './components/v2/EntityPicker';
import { IPickerProps } from './types';

export type OwnBankDetailsPickerProps = Omit<
  IPickerProps<TenantBankAccount, false>,
  'multiple' | 'prefilter'
>;

export function OwnBankDetailsPicker(props: OwnBankDetailsPickerProps) {
  const { data, ...rest } = props;

  const [searchTerm, setSearchTerm] = useState('');

  const allFields = useMemo(() => {
    return { ...FIELDS, ...data };
  }, [data]);

  const handleTransformResponse = useCallback(
    (response: IResponse<TenantBankAccount<EMode.entity>>) => ({
      ...response,
      data: response.data.filter(
        (x) =>
          x.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          x.iban.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    }),
    [searchTerm]
  );

  const renderItem = (item: TenantBankAccount) => {
    return (
      <Tooltip title={item.iban} showOnlyOnOverflow enterNextDelay={1000}>
        <Stack
          direction="row"
          justifyContent="space-between"
          style={{ width: '100%' }}
        >
          <Typography variant="body2" noWrap>
            {item.name}
          </Typography>
          <Typography variant="body2" noWrap className={styles.secondaryInfo}>
            {item.iban}
          </Typography>
        </Stack>
      </Tooltip>
    );
  };

  return (
    <EntityPicker<TenantBankAccount, false>
      data={allFields}
      entity={Entities.tenantBankAccount}
      filterBy={['name', 'iban']}
      sortBy="name"
      renderItemContent={renderItem}
      transformResponse={handleTransformResponse}
      onSearchValueChange={setSearchTerm}
      {...rest}
    />
  );
}

const FIELDS: TenantBankAccount = {
  id: null,
  name: null,
  iban: null,
};
